import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

interface Flight {
    "company name": string;
    date: string;
    price: number;
    destination: string;
}

interface FlightsPriceTrendsProps {
    data: {
        flightsData: Flight[];
    };
}

enum Airlines {
    EL_AL = "El Al Israel",
    OTHER = "Other Airlines"
}

const processFlightData = (data: Flight[]) => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    const monthlyPrices: Record<string, Record<string, number[]>> = {
        [Airlines.EL_AL]: {},
        [Airlines.OTHER]: {}
    };

    data.forEach(flight => {
        const flightDate = new Date(flight.date);
        if (flightDate >= threeMonthsAgo && flightDate <= today) {
            const month = `${flightDate.getFullYear()}-${('0' + (flightDate.getMonth() + 1)).slice(-2)}`;
            const airline = flight['company name'] === Airlines.EL_AL ? Airlines.EL_AL : Airlines.OTHER;

            if (!monthlyPrices[airline][month]) {
                monthlyPrices[airline][month] = [];
            }
            monthlyPrices[airline][month].push(flight.price);
        }
    });

    const labels = Array.from(new Set([...Object.keys(monthlyPrices[Airlines.EL_AL]), ...Object.keys(monthlyPrices[Airlines.OTHER])])).sort();
    const elAlData = labels.map(month => {
        const prices = monthlyPrices[Airlines.EL_AL][month] || [];
        return prices.reduce((sum, price) => sum + price, 0) / (prices.length || 1);
    });
    const otherAirlinesData = labels.map(month => {
        const prices = monthlyPrices[Airlines.OTHER][month] || [];
        return prices.reduce((sum, price) => sum + price, 0) / (prices.length || 1);
    });

    return { labels, elAlData, otherAirlinesData };
};

const FlightsPriceTrends: React.FC<FlightsPriceTrendsProps> = ({ data }) => {
    const { flightsData } = data;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartInstanceRef = useRef<Chart | null>(null);

    useEffect(() => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        const { labels, elAlData, otherAirlinesData } = processFlightData(flightsData);

        if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                chartInstanceRef.current = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels,
                        datasets: [
                            {
                                label: 'Client',
                                data: elAlData,
                                borderColor: 'rgba(255,158,12,0.61)',
                                backgroundColor: '#ffb3419c',
                                borderWidth: 2,
                                fill: false,
                            },
                            {
                                label: 'Market',
                                data: otherAirlinesData,
                                borderColor: '#2f5e9299',
                                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                                borderWidth: 2,
                                fill: false,
                            },
                        ],
                    },
                    options: {
                        plugins: {
                            title: {
                                display: true,
                                text: 'Flight life-cycle - Client vs Market',
                                font: {
                                    size: 20,
                                },
                                padding: {
                                    top: 20,
                                    bottom: 30,
                                },
                            },
                            legend: {
                                display: true,
                                position: 'top',
                                labels: {
                                    font: {
                                        size: 14,
                                    },
                                    usePointStyle: true,
                                    padding: 20,
                                },
                            },
                            tooltip: {
                                callbacks: {
                                    // @ts-ignore
                                    label: (context) => `${context.dataset.label}: $${context.raw.toFixed(2)}`,
                                },
                            },
                        },
                        scales: {
                            y: {
                                beginAtZero: true,
                                title: {
                                    display: true,
                                    text: 'Average Price',
                                    font: {
                                        size: 16,
                                    },
                                },
                            },
                            x: {
                                title: {
                                    display: true,
                                    text: 'Month',
                                    font: {
                                        size: 16,
                                    },
                                },
                            },
                        },
                    },
                });
            }
        }

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [flightsData]);

    return (
        <div>
            <canvas ref={canvasRef} width="800" height="800"></canvas>
        </div>
    );
};

export default FlightsPriceTrends;
